import { CellEditingStoppedEvent, ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, MaximizeIcon, RestoreIcon } from "src/components/common/common";
import { GridCalendar } from "src/components/common/form/GridCalendar";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import {
	CreateCellClickHandler,
	CreateCellDoubleClickHandler,
	CustomCellClickAction,
	CustomCellDoubleClickAction,
	InsertRowHandler,
	RemoveRowHandler,
	createCalendarCellClickHandler,
	createCellCssClassRules,
	createGridDropDownCommitAfter,
	GridCellConfig,
	useRowSelectionHandler,
} from "src/utils/grid/GridHandlerContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { defaultEQM4010_R01DetailDto, EQM4010_R01DetailDto } from "./Dto_EQM4010_R01";
import { useEQM4010_R01Store, useEQM4010_R01Store_GridCalendar } from "./Store_EQM4010_R01";
import { formatLocalDate } from "src/utils/date/dateUtils";
import { EQM4010_R01_EXCEL_HEADER_NAMES } from "./ExcelConfig_EQM4010_R01";
import { useEQM4010_R01Modal } from "./hooks/useEQM4010_R01Modal";
import { cellDateComparator } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'EQM4010_R01-detail-grid-column-state';


const dateColumns = ['cleanDt'];
const commonInputColumns = ['cleanType', 'cleanWorker', 'shotCount', 'remark'];
const defaultEditableColumns = ['cleanType', 'cleanWorker', 'shotCount', 'remark'];

interface Props {
	gridRef: React.RefObject<AgGridReact<EQM4010_R01DetailDto> | null>;
	defaultEQM4010_R01DetailRow: EQM4010_R01DetailDto;
	isMaximized?: boolean;
	onToggleMaximize?: () => void;
}

export const DetailTable_EQM4010_R01 = (props: Props) => {
	const { gridRef, defaultEQM4010_R01DetailRow, isMaximized = false, onToggleMaximize } = props;

	const {
		EQM4010_R01DetailList, setEQM4010_R01DetailList,
		selectedEQM4010_R01DetailList, initSelectedEQM4010_R01DetailList,
		addSelectedEQM4010_R01DetailList, removeSelectedEQM4010_R01DetailList,
		deletedEQM4010_R01DetailList, addDeletedEQM4010_R01DetailList,
		detailColumnState, setDetailColumnState,
		originalEQM4010_R01DetailList,
		insertEQM4010_R01DetailList, removeEQM4010_R01Detail, updateEQM4010_R01Detail,
		detailScrollTop, setDetailScrollTop,
	} = useEQM4010_R01Store();

	const [clickable, setClickable] = useState(true);
	const { cleanTypeList } = useCommonCodeStore();
	const { EQM4010_R01GridCalendarState, setEQM4010_R01GridCalendarState } = useEQM4010_R01Store_GridCalendar();
	const { pickerVisible, pickerPosition, targetRowIndex, targetField, targetDate } = EQM4010_R01GridCalendarState;
	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent<EQM4010_R01DetailDto>({
		gridRef,
		key: 'valueIndex',
		gridData: EQM4010_R01DetailList,
		defaultGridData: defaultEQM4010_R01DetailDto,
		selectedRow: undefined,
		originalGridData: originalEQM4010_R01DetailList,
		updateRow: (index: number, data: EQM4010_R01DetailDto) => updateEQM4010_R01Detail(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['moldCleanNo', 'compNm', 'moldCd', 'moldNm', 'cleanWorkerNm'],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],

		pointerCells: ['cleanType', 'cleanDt', 'cleanWorker', 'shotCount', 'remark'],
		insertedPointerCells: [''],
		commonPointerCells: [''],

		alignLeftCells: [''],
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<EQM4010_R01DetailDto>({
			idKey: 'valueIndex',
			selectedList: selectedEQM4010_R01DetailList,
			deletedList: deletedEQM4010_R01DetailList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});

		return { ...defaultColDef, cellClassRules: { ...baseRules } };
	}, [
		selectedEQM4010_R01DetailList, deletedEQM4010_R01DetailList,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns, createCellClassRules,
	]);

	const handleRowClick = useRowSelectionHandler({
		dataList: EQM4010_R01DetailList,
		idKey: 'valueIndex',
		selectedList: selectedEQM4010_R01DetailList,
		deletedList: deletedEQM4010_R01DetailList,
		addSelected: addSelectedEQM4010_R01DetailList,
		removeSelected: removeSelectedEQM4010_R01DetailList,
		clickable, setClickable,
	});

	const cellEventHandlers = useDetailTableEvents({
		detailList: EQM4010_R01DetailList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: { enableArrowNavigation: true },
	});

	const isEditableRow = useCallback((params: any): boolean => {
		if (params.data?.inserted) return true;
		if (deletedEQM4010_R01DetailList.includes(params.data?.valueIndex?.toString())) return false;
		return true;
	}, [deletedEQM4010_R01DetailList]);

	const validateDateColumns = useCallback((field: string, data: any): boolean => {
		if (!dateColumns.includes(field)) return false;
		if (deletedEQM4010_R01DetailList.includes(data?.valueIndex?.toString())) return false;
		return true;
	}, [deletedEQM4010_R01DetailList]);

	const handleCellEditingStopped = useCallback(
		(e: CellEditingStoppedEvent<EQM4010_R01DetailDto>) => {
			if (e.colDef.field !== 'cleanDt') return;

			setTimeout(() => {
				const gridDiv = gridWrapperRef.current;
				if (!gridDiv) return;

				const cellSelector = `[row-index="${e.rowIndex}"] [col-id="cleanDt"]`;
				const cellElement = gridDiv.querySelector<HTMLElement>(cellSelector);

				if (cellElement) {
					const cellRect = cellElement.getBoundingClientRect();
					const dateValue = e.data?.cleanDt;

					setEQM4010_R01GridCalendarState({
						pickerPosition: {
							top: cellRect.bottom + window.scrollY,
							left: cellRect.left + window.scrollX,
						},
						targetRowIndex: e.rowIndex!,
						targetField: 'cleanDt',
						targetDate: dateValue ? new Date(dateValue) : new Date(),
						pickerVisible: true,
					});
				}
			}, 0);
		},
		[setEQM4010_R01GridCalendarState],
	);

	const cellClickActions: CustomCellClickAction<EQM4010_R01DetailDto>[] = useMemo(() => [
		{
			validate: validateDateColumns,
			action: createCalendarCellClickHandler('cleanDt', EQM4010_R01GridCalendarState, setEQM4010_R01GridCalendarState),
		},
		{
			validate: (field, data) =>
				commonInputColumns.includes(field)
				&& !deletedEQM4010_R01DetailList.includes(data?.valueIndex?.toString()),
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); },
		},
	], [
		EQM4010_R01GridCalendarState, setEQM4010_R01GridCalendarState, validateDateColumns, deletedEQM4010_R01DetailList,
	]);

	const handleCellClick = CreateCellClickHandler(EQM4010_R01DetailList, 'valueIndex', cellClickActions);

	const { settingCodeSearchModalProps } = useEQM4010_R01Modal({
		updateEQM4010_R01Detail,
		gridRef,
	});

	const cellDoubleClickActions: CustomCellDoubleClickAction<EQM4010_R01DetailDto>[] = useMemo(() => [
		{
			validate: (field) => field === 'cleanWorker',
			action: (_e, foundData) => {
				settingCodeSearchModalProps(
					'작업자',
					foundData.cleanWorker || '',
					false,
					'cleanWorker',
					foundData,
					foundData.valueIndex!,
				);
			},
		},
	], [settingCodeSearchModalProps]);

	const handleCellDoubleClick = CreateCellDoubleClickHandler(
		EQM4010_R01DetailList,
		'valueIndex',
		cellDoubleClickActions,
	);

	const handleGridDropDownCommitAfter = useMemo(
		() => createGridDropDownCommitAfter<EQM4010_R01DetailDto>(defaultEditableColumns)
	,[]);

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
		InsertRowHandler({
			event: e,
			dataList: EQM4010_R01DetailList,
			rowData: EQM4010_R01DetailList[rowIndex],
			insertRow: insertEQM4010_R01DetailList,
			defaultRowData: defaultEQM4010_R01DetailRow,
			setClickable,
		});
	}, [EQM4010_R01DetailList, insertEQM4010_R01DetailList, defaultEQM4010_R01DetailRow]);

	const handleCellChange = useCallback((event: any) => {
		const { data, colDef, newValue } = event;
		if (!data || !colDef.field) return;

		const originalData = originalEQM4010_R01DetailList.find((item) => item.valueIndex === data.valueIndex);
		const field = colDef.field as keyof EQM4010_R01DetailDto;
		const isModified = data.inserted === true
			? false
			: (originalData ? originalData[field] !== newValue : true);

		updateEQM4010_R01Detail(data.valueIndex!, {
			...data,
			[field]: newValue,
			isModified,
		});
	}, [originalEQM4010_R01DetailList, updateEQM4010_R01Detail]);

	const baseColumnDefs: ColDef<EQM4010_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: EQM4010_R01DetailDto) => renderIndexCell(
				params,
				(innerParams: EQM4010_R01DetailDto) => getRowStyleFlags(innerParams, {
					deletedList: deletedEQM4010_R01DetailList,
					selectedList: selectedEQM4010_R01DetailList,
					dataList: EQM4010_R01DetailList,
				}),
				(rowIndex: number, e: React.MouseEvent) => insertRowHandler(rowIndex, e),
				(rowData) => RemoveRowHandler(
					gridRef,
					EQM4010_R01DetailList,
					rowData,
					setClickable,
					removeEQM4010_R01Detail,
					(valueIndex) => addDeletedEQM4010_R01DetailList([valueIndex.toString()])
				),
			)
		),
		{ headerName: '세척No.', field: 'moldCleanNo', colId: 'moldCleanNo', width: 130, editable: false },
		{ headerName: '사업장', field: 'compNm', colId: 'compNm', width: 110, editable: false },
		{ headerName: '금형코드', field: 'moldCd', colId: 'moldCd', width: 110, editable: false },
		{ headerName: '금형명', field: 'moldNm', colId: 'moldNm', width: 150, editable: false },
		{
			headerName: '세척구분', field: 'cleanType', colId: 'cleanType', width: 100,
			...CommonCodeColumn.options(
				cleanTypeList,
				isEditableRow,
				CommonCodeColumn.change(updateEQM4010_R01Detail),
				handleGridDropDownCommitAfter,
			),
		},
		{
			headerName: '세척일자', field: 'cleanDt', colId: 'cleanDt', width: 120,
			editable: isEditableRow,
			valueFormatter: (params: any) =>
				params.data?.cleanDt ? formatLocalDate(new Date(params.data.cleanDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '세척현재타수', field: 'shotCount', colId: 'shotCount', width: 110,
			type: 'numericColumn',
			editable: isEditableRow,
			valueFormatter: (params: any) =>
				params.value != null ? Number(params.value).toLocaleString() : '',
		},
		{ headerName: '작업자', field: 'cleanWorker', colId: 'cleanWorker', width: 100, editable: isEditableRow },
		{ headerName: '작업자명', field: 'cleanWorkerNm', colId: 'cleanWorkerNm', width: 100, editable: false },
		{ headerName: '비고', field: 'remark', colId: 'remark', width: 250, editable: isEditableRow },
	], [EQM4010_R01DetailList, selectedEQM4010_R01DetailList, deletedEQM4010_R01DetailList, isEditableRow, cleanTypeList, handleGridDropDownCommitAfter, updateEQM4010_R01Detail, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	,[baseColumnDefs]);

	const columnDefs: ColDef[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<EQM4010_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames: EQM4010_R01_EXCEL_HEADER_NAMES,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [detailColumnState, baseColumnDefs]);

	const saveColumnState = createSaveColumnState(gridRef, COLUMN_STATE_KEY, setDetailColumnState);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '계',
		moldCleanNo: EQM4010_R01DetailList.length.toLocaleString() + "건",
		editable: false,
	}]), [EQM4010_R01DetailList]);

	return (
		<>
			<GridContextWrapper<EQM4010_R01DetailDto>
				gridWrapperRef={gridWrapperRef}
				gridRef={gridRef}
				paddedGridData={paddedGridData}
				enableCellCalculation
				setPaddedGridData={setEQM4010_R01DetailList}
				initSelected={initSelectedEQM4010_R01DetailList}
				defaultRowData={defaultEQM4010_R01DetailRow}
				baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
				defaultColumnState={defaultColumnState}
				setColumnState={setDetailColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Excel<EQM4010_R01DetailDto>
					ref={gridRef}
					rowData={paddedGridData}
					getRowId={getRowId}
					columnDefs={columnDefs}
					defaultColDef={cellCssDefaultColDef}
					rowClassRules={createRowClassRules<EQM4010_R01DetailDto>({
						idKey: 'valueIndex',
						selectedList: selectedEQM4010_R01DetailList,
						deletedList: deletedEQM4010_R01DetailList,
					})}
					onRowClicked={handleRowClick}
					onCellClicked={handleCellClick}
					onCellDoubleClicked={handleCellDoubleClick}
					onCellKeyDown={cellEventHandlers.handleCellKeyDown}
					onCellValueChanged={handleCellChange}
					onCellEditingStopped={handleCellEditingStopped}
					pinnedBottomRowData={pinnedBottomRow}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={saveColumnState}
					getDefaultColumnState={() => defaultColumnState}
					scrollTop={detailScrollTop}
					setScrollTop={setDetailScrollTop}
				/>
			</GridContextWrapper>

			<GridCalendar
				rowIndex={targetRowIndex!}
				targetField={targetField!}
				visible={pickerVisible}
				setVisible={(visible) => setEQM4010_R01GridCalendarState({ pickerVisible: visible })}
				value={targetDate}
				pickerPosition={pickerPosition}
				setPickerPosition={(position) => setEQM4010_R01GridCalendarState({ pickerPosition: position })}
				onClear={() => setEQM4010_R01GridCalendarState({ targetDate: undefined })}
				onChange={(date, rowIndex, targetFieldName) => {
					if (rowIndex !== null && targetFieldName) {
						const foundData = EQM4010_R01DetailList[rowIndex];
						if (foundData) {
							updateEQM4010_R01Detail(foundData.valueIndex!, {
								...foundData,
								[targetFieldName]: date ?? new Date(),
								isModified: true,
							});
						}
					}
				}}
				onClickLoad={() => {}}
			/>
		</>
	);
};
